import { useState, useEffect } from 'react';
import './ScoreView.css';
import { calculateFinalTeamScores } from '../../utils/draftCalculations';
import {
  saveScoreFile,
  listScoreFiles,
  loadScoreFile,
  deleteScoreFile,
  renameScoreFile
} from '../../services/scoreFileService';

const REQUIRED_POSITIONS = ['QB', 'RB', 'WR', 'TE', 'K'];

const validateFinalPoints = (data) =>
  REQUIRED_POSITIONS.every(pos =>
    Array.isArray(data[pos]) &&
    data[pos].every(player =>
      player.hasOwnProperty('name') && player.hasOwnProperty('points')
    )
  );

const ScoreView = ({ username, config, draftedPlayers, playersByPosition, onFinalPointsLoaded, onClose }) => {
  const [scores, setScores] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [savedFiles, setSavedFiles] = useState([]);
  const [loadingFileName, setLoadingFileName] = useState(null);

  useEffect(() => {
    listScoreFiles(username)
      .then(setSavedFiles)
      .catch(error => console.error('Error listing score files:', error));
  }, [username]);

  const scoreWith = (data, name) => {
    setScores(calculateFinalTeamScores(config, draftedPlayers, playersByPosition, data));
    setFileName(name);
    onFinalPointsLoaded(data);
  };

  const handleFinalPointsUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const data = JSON.parse(await file.text());
      if (!validateFinalPoints(data)) {
        alert('Invalid final points file. Expected the JSON produced by get_final_player_point.py.');
        return;
      }
      const name = file.name.replace(/\.json$/i, '');
      scoreWith(data, name);

      // Store the file in the user's account so it can be reused later. An
      // existing file with the same name is overwritten (the natural way to
      // update final points).
      try {
        await saveScoreFile(username, name, data);
        setSavedFiles(await listScoreFiles(username));
      } catch (error) {
        console.error('Error saving score file to account:', error);
        alert('Scored with the file for this session, but saving it to your account failed.');
      }
    } catch (error) {
      console.error('Error reading final points file:', error);
      alert('Error reading file. Please ensure it is a valid JSON file.');
    }
  };

  const handleSelectSavedFile = async (name) => {
    try {
      setLoadingFileName(name);
      const data = await loadScoreFile(username, name);
      if (!validateFinalPoints(data)) {
        alert('This file no longer matches the required structure. Please upload it again.');
        return;
      }
      scoreWith(data, name);
    } catch (error) {
      console.error('Error loading score file:', error);
      alert('Failed to load score file. Please try again.');
    } finally {
      setLoadingFileName(null);
    }
  };

  const handleRenameFile = async (name) => {
    const newName = window.prompt('New file name:', name)?.trim();
    if (!newName || newName === name) return;
    if (savedFiles.some(f => f.name === newName)) {
      alert('A file with this name already exists.');
      return;
    }
    try {
      await renameScoreFile(username, name, newName);
      setSavedFiles(files => files.map(f => (f.name === name ? { ...f, name: newName } : f)));
      if (fileName === name) {
        setFileName(newName);
      }
    } catch (error) {
      console.error('Error renaming score file:', error);
      alert(`Failed to rename score file: ${error.message}`);
    }
  };

  const handleDeleteFile = async (name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    try {
      await deleteScoreFile(username, name);
      setSavedFiles(files => files.filter(f => f.name !== name));
    } catch (error) {
      console.error('Error deleting score file:', error);
      alert('Failed to delete score file. Please try again.');
    }
  };

  const unmatchedPlayers = scores
    ? [...new Set(scores.flatMap(score => score.unmatched))]
    : [];

  return (
    <div className="score-view-overlay">
      <div className="score-view">
        <div className="score-view-header">
          <h2>Final Scores</h2>
          <button className="close-button" onClick={onClose}>
            Close
          </button>
        </div>

        {savedFiles.length > 0 && (
          <div className="score-saved-files">
            {savedFiles.map(file => (
              <div
                key={file.name}
                className={`score-saved-file ${fileName === file.name ? 'selected' : ''}`}
              >
                <button
                  className="score-saved-file-select"
                  onClick={() => handleSelectSavedFile(file.name)}
                  disabled={loadingFileName !== null}
                >
                  {fileName === file.name ? '✓ ' : ''}
                  {file.name}
                  {loadingFileName === file.name && <span className="score-saved-file-loading"> Loading...</span>}
                </button>
                <span className="score-saved-file-date">
                  {new Date(file.lastModified).toLocaleDateString()}
                </span>
                <button className="score-saved-file-action" onClick={() => handleRenameFile(file.name)}>
                  Rename
                </button>
                <button className="score-saved-file-action delete" onClick={() => handleDeleteFile(file.name)}>
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}

        <input
          type="file"
          accept=".json"
          onChange={handleFinalPointsUpload}
          id="final-points-upload"
          className="score-upload-input"
        />

        {!scores ? (
          <div className="score-upload-prompt">
            <p>
              Upload a final player points file for the season
              (generated by bin/get_final_player_point.py) to score
              each team with the points their roster actually produced.
              {savedFiles.length > 0 && ' Or select a previously uploaded file above.'}
              {' '}Uploaded files are saved to your account.
            </p>
            <label htmlFor="final-points-upload" className="score-upload-label">
              Upload Final Points File
            </label>
          </div>
        ) : (
          <>
            <p className="score-file-name">Scored with {fileName}</p>
            <table className="score-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Team</th>
                  <th>Official</th>
                  <th>With Bench</th>
                </tr>
              </thead>
              <tbody>
                {scores.map((score, rank) => (
                  <tr key={score.teamIndex}>
                    <td>{rank + 1}</td>
                    <td>{score.teamName}</td>
                    <td>{score.official.toFixed(2)}</td>
                    <td>{score.withBench.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="score-note">
              Official counts starting slots only; bench points are shown in With Bench.
            </p>
            {unmatchedPlayers.length > 0 && (
              <div className="score-unmatched">
                <strong>Not found in the final points file (scored 0):</strong>
                <p>{unmatchedPlayers.join(', ')}</p>
              </div>
            )}
            <label htmlFor="final-points-upload" className="score-upload-label">
              Score With a Different File
            </label>
          </>
        )}
      </div>
    </div>
  );
};

export default ScoreView;
